/**
 * Command palette context and hooks.
 *
 * - Cmd+K (Mac) / Ctrl+K (Windows) opens the palette
 * - Escape closes it
 * - Supports vim-style navigation shortcuts (g + key)
 */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react'
import { useNavigate } from 'react-router-dom'

interface CommandPaletteContextValue {
  isOpen: boolean
  open: () => void
  close: () => void
  toggle: () => void
}

const CommandPaletteContext = createContext<CommandPaletteContextValue | null>(null)

// Navigation shortcuts: g + key -> path
const VIM_SHORTCUTS: Record<string, string> = {
  d: '/',           // g d -> Dashboard
  h: '/holdings',   // g h -> Holdings
  w: '/watchlist',  // g w -> Watchlist
  r: '/research',   // g r -> Research
  n: '/news',       // g n -> News
  a: '/analytics',  // g a -> Analytics
  p: '/portfolios', // g p -> Portfolios
  t: '/strategy/theses',      // g t -> Theses
  l: '/strategy/allocations', // g l -> Allocations
  s: '/settings',   // g s -> Settings
}

interface CommandPaletteProviderProps {
  children: ReactNode
  /** Enable vim-style navigation shortcuts (g + key) */
  enableVimShortcuts?: boolean
}

export function CommandPaletteProvider({
  children,
  enableVimShortcuts = true,
}: CommandPaletteProviderProps) {
  const [isOpen, setIsOpen] = useState(false)
  const navigate = useNavigate()

  // Track if we're waiting for the second key of a vim shortcut
  const pendingVimKey = useRef<string | null>(null)
  const vimTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  const open = useCallback(() => setIsOpen(true), [])
  const close = useCallback(() => setIsOpen(false), [])
  const toggle = useCallback(() => setIsOpen(prev => !prev), [])

  // Clear vim shortcut state
  const clearVimState = useCallback(() => {
    pendingVimKey.current = null
    if (vimTimeout.current) {
      clearTimeout(vimTimeout.current)
      vimTimeout.current = null
    }
  }, [])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in inputs or selects
      const target = event.target as HTMLElement
      const isInput = target.tagName === 'INPUT' ||
                     target.tagName === 'TEXTAREA' ||
                     target.tagName === 'SELECT' ||
                     target.isContentEditable

      // Cmd+K / Ctrl+K opens command palette (works even in inputs)
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault()
        toggle()
        clearVimState()
        return
      }

      // Escape closes the palette
      if (event.key === 'Escape' && isOpen) {
        event.preventDefault()
        close()
        clearVimState()
        return
      }

      // Skip vim shortcuts if in input or palette is open
      if (isInput || isOpen || !enableVimShortcuts) {
        return
      }

      // Vim-style navigation: g + key
      if (pendingVimKey.current === 'g') {
        const path = VIM_SHORTCUTS[event.key.toLowerCase()]
        if (path) {
          event.preventDefault()
          navigate(path)
        }
        clearVimState()
        return
      }

      // Start vim shortcut sequence
      if (event.key === 'g' && !event.metaKey && !event.ctrlKey && !event.altKey) {
        pendingVimKey.current = 'g'
        // Clear after 1 second if no second key pressed
        vimTimeout.current = setTimeout(clearVimState, 1000)
        return
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      if (vimTimeout.current) {
        clearTimeout(vimTimeout.current)
      }
    }
  }, [isOpen, toggle, close, navigate, enableVimShortcuts, clearVimState])

  return (
    <CommandPaletteContext.Provider value={{ isOpen, open, close, toggle }}>
      {children}
    </CommandPaletteContext.Provider>
  )
}

export function useCommandPalette(): CommandPaletteContextValue {
  const context = useContext(CommandPaletteContext)
  if (!context) {
    throw new Error('useCommandPalette must be used within a CommandPaletteProvider')
  }
  return context
}
